import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaPlus,
  FaSearch,
  FaSpinner,
  FaHandshake,
} from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import useApp from "../../hooks/useAppContext";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import LinhaAgenda from "../agenda/linhaAgenda";
import LinhaAgendaConteudo from "../agenda/linhaAgendaConteudo";
import "../adm/admPatrocinadoresList.css";
import "../adm/adm.css";
import "./recepcao.css";

function labelDiaSlot(data) {
  if (!data) return "—";
  const partes = String(data).split("-");
  if (partes.length !== 3) return data;
  return `${partes[2]}/${partes[1]}`;
}

function Recepcao() {
  const navigate = useNavigate();
  const { dia } = useParams();
  const { usuario } = useAuth();
  const { setMostraMenuAdm, getAgendaRecepcao, getDiasEvento } = useApp();

  const [diasEvento, setDiasEvento] = useState([]);
  const [reunioes, setReunioes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [isLoadingDias, setIsLoadingDias] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const temPatrocinador = Boolean(usuario?.id_patrocinador);

  const carregarDias = useCallback(async () => {
    setIsLoadingDias(true);
    setError(null);
    try {
      const [dadosDias, respSlots] = await Promise.all([
        getDiasEvento(),
        api.get("/slots"),
      ]);

      const dias = dadosDias?.dias || [];
      const listaSlots = respSlots?.data?.dados || [];
      const datasSlots = [
        ...new Set(listaSlots.map((s) => s.data).filter(Boolean)),
      ].sort();

      const diasComSlots = dias.filter((item) =>
        datasSlots.includes(item.data)
      );

      const diasResolvidos =
        diasComSlots.length > 0
          ? diasComSlots
          : datasSlots.map((data) => ({
              data,
              dia: String(data).substring(8, 10),
              label: labelDiaSlot(data),
            }));

      setDiasEvento(diasResolvidos);
      return diasResolvidos;
    } catch (err) {
      console.error("Erro ao carregar dias da recepção:", err);
      setError("Erro ao carregar dias do evento com slots.");
      setDiasEvento([]);
      return [];
    } finally {
      setIsLoadingDias(false);
    }
  }, [getDiasEvento]);

  const carregarEventos = useCallback(async () => {
    if (!usuario || !dia) return;

    setIsLoading(true);
    setError(null);
    try {
      const idPatrocinador = usuario.id_patrocinador || null;
      const tipo = idPatrocinador
        ? ["reuniao_negocio"]
        : ["conteudo_patrocinado", "conteudo_editorial", "esporte"];

      const auxEventos = (await getAgendaRecepcao(dia, idPatrocinador, tipo)) || [];
      const ordenacao = auxEventos
        .filter((ae) => ae.nome !== "Reunião de almoço/jantar")
        .sort((a, b) => {
          const dataA = new Date(
            a.dt_hora_inicio ? a.dt_hora_inicio : a.agenda?.dt_hora_inicio
          );
          const dataB = new Date(
            b.dt_hora_inicio ? b.dt_hora_inicio : b.agenda?.dt_hora_inicio
          );
          return dataA < dataB ? -1 : dataA > dataB ? 1 : 0;
        });

      setReunioes(ordenacao);
    } catch (err) {
      console.error("Erro ao carregar agenda da recepção:", err);
      setError("Erro ao carregar a agenda da recepção.");
      setReunioes([]);
    } finally {
      setIsLoading(false);
    }
  }, [usuario, dia, getAgendaRecepcao]);

  useEffect(() => {
    setMostraMenuAdm(false);
  }, [setMostraMenuAdm]);

  useEffect(() => {
    const iniciar = async () => {
      const dias = await carregarDias();
      if (!dias.length) return;

      const diaValido = dias.some((item) => item.dia === dia);
      if (!dia || !diaValido) {
        navigate(`/private/adm/recepcao/${dias[0].dia}`, { replace: true });
      }
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarDias]);

  useEffect(() => {
    carregarEventos();
  }, [carregarEventos]);

  const reunioesFiltradas = useMemo(() => {
    if (!filtro.trim()) return reunioes;
    const termo = filtro.toLowerCase().trim();
    return reunioes.filter((item) => {
      const agenda = item.agenda || item;
      const participantes = agenda.participantes || item.participantes || [];
      const nomes = participantes
        .map((p) => p?.usuario?.nome || p?.usuario?.empresa || "")
        .join(" ");
      const campos = [agenda.nome, agenda.tipo, nomes, item.nome];
      return campos.some((campo) =>
        String(campo || "")
          .toLowerCase()
          .includes(termo)
      );
    });
  }, [reunioes, filtro]);

  return (
    <div className="apl-page apl-page--padded recepcao-page">
      <header className="apl-header" aria-label="Cabeçalho da recepção">
        <div className="apl-header-text">
          <h1>Recepção</h1>
          <p>
            {temPatrocinador
              ? "Confira as reuniões de negócio do dia e faça o check-in dos participantes."
              : "Confira os conteúdos e agendas de esporte do dia e faça o check-in dos participantes."}
          </p>
        </div>

        <div className="apl-toolbar apl-toolbar--inline">
          <div className="apl-search">
            <span className="apl-search-icon" aria-hidden>
              <FaSearch size={14} />
            </span>
            <input
              type="search"
              className="apl-search-input"
              placeholder="Buscar por evento ou participante…"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              disabled={isLoading || isLoadingDias}
              aria-label="Buscar na agenda da recepção"
            />
          </div>

          <div className="apl-toolbar-actions">
            {!temPatrocinador ? (
              <Link to="/private/adm/m_m" className="apl-btn apl-btn--secondary">
                <FaHandshake size={14} aria-hidden />
                M&M
              </Link>
            ) : null}

            {temPatrocinador ? (
              <Link
                to={`/private/adm/recepcao-incluir-negocio/${usuario.id_patrocinador}`}
                className="apl-btn apl-btn--primary"
              >
                <FaPlus size={14} aria-hidden />
                Nova reunião
              </Link>
            ) : null}

            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              title="Voltar"
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </div>
      </header>

      <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
        {error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : null}

        {!isLoadingDias && diasEvento.length > 0 ? (
          <nav className="apl-day-tabs" aria-label="Dias do evento com slots">
            {diasEvento.map((item) => (
              <Link
                key={item.data}
                to={`/private/adm/recepcao/${item.dia}`}
                className={`apl-day-tab${dia === item.dia ? " is-active" : ""}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        ) : null}

        {isLoading || isLoadingDias ? (
          <div className="apl-card">
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={28} aria-hidden />
              <span>
                {isLoadingDias
                  ? "Carregando dias com slots…"
                  : "Carregando agenda da recepção…"}
              </span>
            </div>
          </div>
        ) : (
          <section className="apl-card" aria-label="Lista da recepção">
            {reunioesFiltradas.length === 0 ? (
              <div className="apl-empty">
                <p className="apl-empty-title">
                  {reunioes.length === 0
                    ? "Nenhum item para esta data"
                    : "Nenhum resultado"}
                </p>
                <p style={{ margin: 0 }}>
                  {reunioes.length === 0
                    ? "Selecione outro dia ou aguarde novos agendamentos."
                    : "Ajuste a busca para ver os itens."}
                </p>
              </div>
            ) : (
              <div className="area-lista-reunioes recepcao-lista">
                {reunioesFiltradas.map((r, index) =>
                  r.id_agenda ? (
                    <LinhaAgenda
                      key={r.id || `ap-${index}`}
                      dia={dia}
                      item={r}
                      handle={() => carregarEventos()}
                    />
                  ) : (
                    <LinhaAgendaConteudo
                      key={r.id || `ag-${index}`}
                      dia={dia}
                      item={r}
                      handle={() => carregarEventos()}
                    />
                  )
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default Recepcao;
